import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Plus, Copy, Archive, Trash2, Loader2, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const statusOf = (f) => f.status === "archived" ? ["Archiviato", "#64748B"]
  : f.published_version ? [`Pubblicato v${f.published_version}${f.meta_mode === "mock" ? " (MOCK)" : ""}${f.draft_version ? ` · bozza v${f.draft_version}` : ""}`, "#15803D"]
  : f.has_frozen ? (f.draft_version ? [`Congelato v${f.frozen_version} · bozza v${f.draft_version}`, "#0369A1"] : ["Congelato (non pubblicato)", "#0369A1"])
  : ["Bozza", "#B45309"];

export default function Forms() {
  const nav = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [items, setItems] = useState(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/forms").then((r) => setItems(r.data.items)).catch((e) => { setItems([]); toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? e?.message)); });
  useEffect(() => { api.get("/forms/config").then((r) => { setCfg(r.data); if (r.data.enabled) load(); else setItems([]); }).catch(() => { setCfg({ enabled: false }); setItems([]); }); }, []);

  const act = async (fn, okMsg) => {
    setBusy(true);
    try { const r = await fn(); if (okMsg) toast.success(okMsg); await load(); return r; } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? e?.message)); } finally { setBusy(false); }
  };
  const create = async () => {
    if (!name.trim()) return;
    const r = await act(() => api.post("/forms", { name }), "Form creato");
    if (r?.data?.id) nav(`/form/${r.data.id}`);
  };

  if (cfg && !cfg.enabled) return <div className="p-6 text-sm text-slate-400" data-testid="forms-disabled">La sezione FORM non è attiva per questa azienda.</div>;
  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="forms-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-100 flex items-center gap-2"><ClipboardList className="w-7 h-7 text-[#38BDF8]" /> FORM — WhatsApp Flows</h1>
          <p className="text-xs text-slate-400 mt-1">Costruttore di questionari WhatsApp Flows collegati alla Rubrica. Validazione e congelamento sono locali; la pubblicazione su Meta avviene in modalità {cfg?.meta?.mode === "mock" ? "MOCK (nessuna chiamata reale)" : "LIVE"}.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => nav("/form/analytics")} data-testid="forms-analytics-btn"><BarChart3 className="w-4 h-4 mr-1" /> Analisi</Button>
          {cfg?.can_edit && (<>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome del nuovo Form" className="w-56" data-testid="forms-new-name" onKeyDown={(e) => e.key === "Enter" && create()} />
            <Button onClick={create} disabled={busy || !name.trim()} data-testid="forms-create-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo</Button>
          </>)}
        </div>
      </div>
      {items === null ? <Loader2 className="w-6 h-6 animate-spin text-slate-400" /> : items.length === 0 ? (
        <p className="text-sm text-slate-400" data-testid="forms-empty">Nessun Form. Crea il primo per iniziare.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[#1F2937]">
          <table className="w-full text-sm" data-testid="forms-table">
            <thead className="bg-[#1E293B] text-slate-400 text-xs"><tr><th className="text-left p-3">Nome</th><th className="text-left p-3">Stato</th><th className="p-3">Versione</th><th className="text-left p-3">Aggiornato</th><th className="text-left p-3">Autore</th><th className="p-3">Azioni</th></tr></thead>
            <tbody>
              {items.map((f) => { const [lab, col] = statusOf(f); return (
                <tr key={f.id} className="border-t border-[#1F2937] hover:bg-slate-100" data-testid={`form-row-${f.id}`}>
                  <td className="p-3"><button className="text-slate-100 font-medium hover:underline text-left" onClick={() => nav(`/form/${f.id}`)} data-testid={`form-open-${f.id}`}>{f.name}</button>{f.description && <p className="text-xs text-slate-500">{f.description}</p>}</td>
                  <td className="p-3"><span className="text-xs px-2 py-0.5 rounded-full whitespace-nowrap" style={{ background: `${col}22`, color: col }} data-testid={`form-status-${f.id}`}>{lab}</span></td>
                  <td className="p-3 text-center text-slate-300">v{f.current_version}</td>
                  <td className="p-3 text-slate-400 text-xs">{new Date(f.updated_at).toLocaleString("it-IT")}</td>
                  <td className="p-3 text-slate-400 text-xs">{f.updated_by || f.created_by}</td>
                  <td className="p-3"><div className="flex justify-center gap-1">
                    {cfg?.can_edit && <Button size="icon" variant="ghost" title="Duplica come nuova bozza" disabled={busy} onClick={() => act(() => api.post(`/forms/${f.id}/duplicate`), "Copia creata")} data-testid={`form-dup-${f.id}`}><Copy className="w-4 h-4" /></Button>}
                    {cfg?.can_admin && f.status !== "archived" && <Button size="icon" variant="ghost" title="Archivia" disabled={busy} onClick={() => window.confirm("Archiviare il Form?") && act(() => api.post(`/forms/${f.id}/archive`), "Archiviato")} data-testid={`form-archive-${f.id}`}><Archive className="w-4 h-4" /></Button>}
                    {cfg?.can_admin && f.status === "draft" && !f.has_frozen && <Button size="icon" variant="ghost" title="Elimina bozza" disabled={busy} onClick={() => window.confirm("Eliminare definitivamente la bozza?") && act(() => api.delete(`/forms/${f.id}`), "Eliminato")} data-testid={`form-delete-${f.id}`}><Trash2 className="w-4 h-4 text-red-400" /></Button>}
                  </div></td>
                </tr>); })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
